import { useCallback, useEffect, useRef, useState } from 'react';
import { lowBattery, movement, nameOf, UserLocation, validCoordinates } from './model';
declare global { interface Window { mapkit: any } }
export default function useAppleMap(items: UserLocation[], tokenProvider: () => string, select: (id: number) => void) {
  const container = useRef<HTMLDivElement>(null), map = useRef<any>(null), markers = useRef(new Map<number, any>()), selectRef = useRef(select);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading'), [attempt, setAttempt] = useState(0);
  const framed = useRef(false), errorText = 'Apple haritası yüklenemedi. Listeyi kullanabilir veya yeniden deneyebilirsiniz.';
  selectRef.current = select;
  useEffect(() => {
    let cancelled = false, timer: ReturnType<typeof setTimeout>, watchdog: ReturnType<typeof setTimeout>, kit: any;
    let tries = 0;
    let retryScript: HTMLScriptElement | null = null;
    // Retry a failed SDK download as well as a failed map initialization.
    if (attempt > 0 && !window.mapkit) {
      retryScript = document.createElement('script');
      const existing = document.querySelector<HTMLScriptElement>('script[src^="https://cdn.apple-mapkit.com/"]');
      retryScript.src = existing?.src || 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js';
      retryScript.crossOrigin = 'anonymous'; retryScript.async = true;
      document.head.appendChild(retryScript);
    }
    const error = () => { if (!cancelled) setStatus('error'); };
    const configured = () => { if (!cancelled && map.current) { clearTimeout(watchdog); setStatus('ready'); } };
    const selected = (event: any) => { for (const [id, marker] of Array.from(markers.current.entries())) if (event.annotation === marker) { selectRef.current(id); break; } };
    const initialize = () => {
      if (cancelled) return;
      if (!window.mapkit || !container.current) { if (++tries < 50) timer = setTimeout(initialize, 200); else error(); return; }
      kit = window.mapkit;
      try {
        kit.addEventListener('error', error); kit.addEventListener('configuration-change', configured);
        kit.init({ authorizationCallback: (done: (token: string) => void) => done(tokenProvider()), language: 'tr' });
        map.current = new kit.Map(container.current, { center: new kit.Coordinate(39.9334, 32.8597), cameraDistance: 5000000, mapType: kit.Map.MapTypes.Standard, showsCompass: kit.FeatureVisibility.Visible, showsScale: kit.FeatureVisibility.Visible, showsMapTypeControl: true, showsZoomControl: true });
        map.current.addEventListener('select', selected);
        watchdog = setTimeout(error, 15000);
      } catch { error(); }
    };
    setStatus('loading'); framed.current = false; timer = setTimeout(initialize, 50);
    return () => {
      cancelled = true; clearTimeout(timer); clearTimeout(watchdog); retryScript?.remove();
      if (kit) { kit.removeEventListener('error', error); kit.removeEventListener('configuration-change', configured); }
      if (map.current) { map.current.removeEventListener('select', selected); map.current.destroy(); map.current = null; }
      markers.current.clear();
    };
  }, [attempt, tokenProvider]);
  const fit = useCallback(() => {
    if (status !== 'ready' || !map.current || !markers.current.size) return;
    try { map.current.showItems(Array.from(markers.current.values()), { animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches, padding: new window.mapkit.Padding(65, 45, 55, 45) }); } catch { setStatus('error'); }
  }, [status]);
  useEffect(() => {
    if (status !== 'ready' || !map.current) return;
    const kit = window.mapkit, valid = items.filter(validCoordinates), ids = new Set(valid.map(item => item.user_id));
    try {
      for (const [id, marker] of Array.from(markers.current.entries())) if (!ids.has(id)) { map.current.removeAnnotation(marker); markers.current.delete(id); }
      for (const item of valid) {
        let marker = markers.current.get(item.user_id);
        const color = lowBattery(item) ? '#c96560' : movement(item) === 'stationary' ? '#8991a0' : movement(item) === 'unknown' ? '#bb934e' : '#8b59ce';
        if (!marker) { marker = new kit.MarkerAnnotation(new kit.Coordinate(item.latitude, item.longitude), { color, title: nameOf(item), subtitle: item.address || '', glyphText: nameOf(item).charAt(0).toLocaleUpperCase('tr-TR'), calloutEnabled: false }); markers.current.set(item.user_id, marker); map.current.addAnnotation(marker); }
        else { marker.coordinate = new kit.Coordinate(item.latitude, item.longitude); marker.title = nameOf(item); marker.subtitle = item.address || ''; marker.color = color; marker.glyphText = nameOf(item).charAt(0).toLocaleUpperCase('tr-TR'); }
      }
      if (!framed.current && markers.current.size) { fit(); framed.current = true; }
    } catch { setStatus('error'); }
  }, [items, status, fit]);
  const focus = useCallback((item: UserLocation) => {
    if (status !== 'ready' || !map.current || !validCoordinates(item)) return;
    const kit = window.mapkit; map.current.region = new kit.CoordinateRegion(new kit.Coordinate(item.latitude, item.longitude), new kit.CoordinateSpan(0.012, 0.012));
  }, [status]);
  return { container, status, errorText, fit, focus, retry: () => setAttempt(value => value + 1) };
}
